import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Solution } from '@/types/evaluation.types';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, CheckCircle2, Circle, Lock, LockOpen, Send } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SolutionCompletion {
  solutionId: string;
  percentage: number;
  scored: number;
  total: number;
}

interface ScorecardHeaderProps {
  evaluationId: string;
  evaluationName: string;
  evaluationStatus?: string;
  participantName: string | null;
  solutions: Solution[];
  currentSolutionId: string;
  onSolutionChange: (solutionId: string) => void;
  scoredCount: number;
  totalCount: number;
  solutionCompletions: SolutionCompletion[];
  overallCompletion: { percentage: number; scored: number; total: number };
  isLocked: boolean;
  isScoringLockedByStatus: boolean;
  canSubmit: boolean;
  canReopen: boolean;
  onSubmit: () => void;
  onReopen: () => void;
}

/**
 * Header for scorecard page with solution selector and progress tracking
 */
export function ScorecardHeader({
  evaluationId,
  evaluationName,
  evaluationStatus,
  participantName,
  solutions,
  currentSolutionId,
  onSolutionChange,
  scoredCount,
  totalCount,
  solutionCompletions,
  overallCompletion,
  isLocked,
  isScoringLockedByStatus,
  canSubmit,
  canReopen,
  onSubmit,
  onReopen,
}: ScorecardHeaderProps) {
  const { t } = useTranslation(['scorecard', 'common']);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);

  const currentSolution = solutions.find((s) => s.id === currentSolutionId);
  const progressPercentage = totalCount > 0 ? Math.round((scoredCount / totalCount) * 100) : 0;

  const getCompletionForSolution = (solutionId: string) => {
    return solutionCompletions.find((c) => c.solutionId === solutionId);
  };

  const handleSubmitClick = () => {
    setShowSubmitDialog(true);
  };

  const handleSubmitConfirm = () => {
    onSubmit();
    setShowSubmitDialog(false);
  };

  const handleReopenClick = () => {
    setShowReopenDialog(true);
  };

  const handleReopenConfirm = () => {
    onReopen();
    setShowReopenDialog(false);
  };

  return (
    <div className="sticky top-0 z-[20] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-3 space-y-3">
        {/* Top row: Back button and title */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/evaluation/${evaluationId}`}>
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('common:navigation.backToDashboard')}
              </Link>
            </Button>

            <div className="h-5 w-px bg-border" aria-hidden="true" />

            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">{evaluationName}</span>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold">{t('scorecard:title')}</h1>
                {participantName && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-sm font-medium text-primary">{participantName}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Solution selector - Highlighted */}
          <div className="flex items-center gap-3 px-4 py-2 bg-primary/10 dark:bg-primary/20 rounded-lg border-2 border-primary/30">
            <span className="text-sm font-semibold text-primary whitespace-nowrap">
              {t('scorecard:solutionLabel')}:
            </span>
            <Select value={currentSolutionId} onValueChange={onSolutionChange}>
              <SelectTrigger className="w-[280px] border-primary/50 bg-background font-medium">
                <SelectValue>
                  {currentSolution
                    ? `${currentSolution.solutionName} - ${currentSolution.vendorName}`
                    : t('scorecard:selectSolution')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {solutions.map((solution) => {
                  const completion = getCompletionForSolution(solution.id);
                  const isComplete = completion && completion.percentage === 100;
                  const hasProgress = completion && completion.percentage > 0;

                  return (
                    <SelectItem key={solution.id} value={solution.id}>
                      <div className="flex items-center justify-between gap-4 w-full py-1">
                        <div className="flex items-center gap-3">
                          {isComplete ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" aria-hidden="true" />
                          ) : hasProgress ? (
                            <Circle className="h-4 w-4 text-amber-500 flex-shrink-0" aria-hidden="true" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                          )}
                          <div className="flex flex-col">
                            <span className="font-medium">{solution.solutionName}</span>
                            <span className="text-xs text-muted-foreground">{solution.vendorName}</span>
                          </div>
                        </div>
                        {completion && (
                          <Badge
                            variant={isComplete ? 'default' : hasProgress ? 'secondary' : 'outline'}
                            className={cn(
                              'text-xs tabular-nums ml-2',
                              isComplete && 'bg-green-600 hover:bg-green-700'
                            )}
                          >
                            {completion.percentage}%
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Progress bar row */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {t('scorecard:progress.currentSolution')}:
                </span>
                <span className="text-muted-foreground">
                  {t('scorecard:progress.count', { scored: scoredCount, total: totalCount })}
                </span>
              </div>
              {solutions.length > 1 && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">|</span>
                  <span className="font-medium">
                    {t('scorecard:progress.overall')}:
                  </span>
                  <span className="text-muted-foreground">
                    {overallCompletion.scored}/{overallCompletion.total}
                  </span>
                  <Badge variant="outline" className="tabular-nums">
                    {overallCompletion.percentage}%
                  </Badge>
                </div>
              )}
            </div>
            <span className="text-sm font-semibold tabular-nums">
              {progressPercentage}%
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Locked status banner */}
        {isLocked && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-green-700 dark:text-green-400" aria-hidden="true" />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-green-900 dark:text-green-100">
                    {t('scorecard:status.locked')}
                  </span>
                  <span className="text-xs text-green-700 dark:text-green-300">
                    {t('scorecard:status.lockedDescription')}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReopenClick}
                disabled={!canReopen}
                className="border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40"
              >
                <LockOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('scorecard:actions.reopen')}
              </Button>
            </div>

            {/* Evaluation Status Lock Alert */}
            {isScoringLockedByStatus && (
              <Alert className="border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20">
                <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-900 dark:text-amber-200">
                  {evaluationStatus === 'Concluded'
                    ? t('dashboard:status.scoringLocked.concluded')
                    : t('dashboard:status.scoringLocked.closed')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Info row */}
        <div className="flex items-center justify-between gap-4">
          {!isScoringLockedByStatus && (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded-md px-3 py-1.5 border border-border/50">
              💡 {t('scorecard:keyboardHint')}
            </div>
          )}
          <div className={`flex items-center gap-3 ${isScoringLockedByStatus ? 'ml-auto' : ''}`}>
            {currentSolution && !isLocked && (
              <div className="text-xs font-medium text-primary flex items-center gap-2 bg-primary/5 px-3 py-1.5 rounded-md border border-primary/20">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                {t('scorecard:currentlyScoring')}: {currentSolution.solutionName}
              </div>
            )}
            {!isLocked && canSubmit && (
              <Button
                onClick={handleSubmitClick}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('scorecard:actions.submit')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Submit confirmation dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('scorecard:confirmations.submitTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('scorecard:confirmations.submitDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitConfirm} className="bg-green-600 hover:bg-green-700">
              {t('scorecard:actions.submit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen confirmation dialog */}
      <AlertDialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('scorecard:confirmations.reopenTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('scorecard:confirmations.reopenDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReopenConfirm}>
              {t('scorecard:actions.reopen')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
